/**
 * activeCompetition — the single place that maps an (Operative_Year, active
 * Tenant) to the canonical Application_Form `competitionId` the director and
 * applicant surfaces bind to.
 *
 * The application-form backend keys every Application_Form, Document_Catalog,
 * and review/approval row by a string `competitionId`. That id is the migration
 * binding convention `comp-${year}-${tenantSlug}` — where the founding Idaho
 * tenant keeps the exact seeded slug `idaho-america` (see
 * `frontend/src/lib/merit/client/fixtures.ts`, `MOCK_COMPETITION_ID`, whose
 * comment documents the `comp-2026-idaho-america → 2026` binding). For 2026 the
 * Idaho id is `comp-2026-idaho-america`.
 *
 * tenant-foundation Task 7 (slice 1) generalized this CLIENT read from the
 * single-tenant `idaho-america` constant to a per-tenant derivation. The tenant
 * comes from the viewer's `custom:activeTenantId` ID-token claim (decoded onto
 * `useAuth().user?.activeTenantId` by AuthContext, minted server-side only after
 * a Membership was validated at login/switch — never client-asserted). So a
 * non-Idaho viewer (e.g. `tenant-meridian`) now READS its own tenant's forms
 * (`comp-${year}-meridian`), which land via that tenant's seed.
 *
 * The backend WRITE side is not yet tenant-scoped: `createFormSchema.req.vtl`
 * still hardcodes the `comp-${year}-idaho-america` binding by deliberate
 * deferral (see that file's note) — generalizing the write is folded into the
 * later tenant-foundation work (tenantId stamping + provisioning + the Task 10
 * enforcement flip), NOT a per-file slug constant. That write path is also not
 * UI-wired today (forms are seed-authored), so only Idaho authoring is exercised
 * and the client/seed READ binding above is the surface this slice moves.
 *
 * This helper is INTENTIONALLY the one and only encoding of that convention in
 * the client surfaces: callers MUST go through here rather than re-deriving the
 * `comp-${year}-<slug>` string inline. Passing NO tenant yields the founding
 * Idaho binding, so an un-threaded caller stays byte-identical to the legacy
 * single-tenant behavior.
 */

/**
 * The competition slug for the founding Idaho tenant — the seeded migration
 * binding `comp-${year}-idaho-america`. Preserved byte-for-byte for back-compat.
 */
export const IDAHO_COMPETITION_SLUG = 'idaho-america';

/**
 * The founding Idaho tenant id. Its competition slug is the legacy
 * `idaho-america` (NOT the mechanical `idaho` a prefix-strip would yield), so it
 * is special-cased below.
 */
export const IDAHO_TENANT_ID = 'tenant-idaho';

/** The `tenant-` prefix every Tenant id carries by convention. */
const TENANT_ID_PREFIX = 'tenant-';

/**
 * Derive the Application_Form competition slug for an active Tenant.
 *
 * - Absent/blank claim, or the founding `tenant-idaho` → `idaho-america`
 *   (BACK-COMPAT: Idaho keeps the exact seeded `comp-${year}-idaho-america`; an
 *   absent claim also defaults to Idaho so a not-yet-resolved viewer never
 *   silently reads a different tenant's forms).
 * - Any other `tenant-<slug>` → `<slug>` (e.g. `tenant-meridian` → `meridian`).
 * - Any other non-blank value → itself (defensive: keeps an unexpected id in its
 *   own namespace rather than colliding with Idaho's forms).
 *
 * @param activeTenantId the viewer's `custom:activeTenantId` claim, or
 *   `undefined`/blank when the viewer is not resolved into a Tenant.
 */
export function competitionSlugForTenant(activeTenantId?: string | null): string {
  const raw = typeof activeTenantId === 'string' ? activeTenantId.trim() : '';
  if (raw === '' || raw === IDAHO_TENANT_ID) {
    return IDAHO_COMPETITION_SLUG;
  }
  if (raw.startsWith(TENANT_ID_PREFIX)) {
    const slug = raw.slice(TENANT_ID_PREFIX.length);
    return slug.length > 0 ? slug : IDAHO_COMPETITION_SLUG;
  }
  return raw;
}

/**
 * Resolve the canonical Application_Form `competitionId` for an Operative_Year
 * and the viewer's active Tenant.
 *
 * Encodes the migration binding `comp-${operativeYear}-${slug}` where `slug` is
 * derived from the active tenant per {@link competitionSlugForTenant}. This is
 * the single client swap point for the tenant-scoped competition binding.
 *
 * @param operativeYear the Season SSOT operative year (from `useActiveSeason()`).
 * @param activeTenantId the viewer's `custom:activeTenantId` claim
 *   (`useAuth().user?.activeTenantId`). OMITTED/blank → the founding Idaho
 *   binding `comp-${operativeYear}-idaho-america` (back-compat default, so an
 *   un-threaded caller stays byte-identical to the single-tenant behavior).
 * @returns the `comp-${operativeYear}-${slug}` competition id.
 */
export function resolveActiveCompetitionId(
  operativeYear: number,
  activeTenantId?: string | null,
): string {
  return `comp-${operativeYear}-${competitionSlugForTenant(activeTenantId)}`;
}
